import { cn } from '@/lib/cn';

interface LogoProps {
  /** "full" = mark + wordmark + tagline, "compact" = mark + wordmark, "mark" = icon only. */
  variant?: 'full' | 'compact' | 'mark';
  className?: string;
  /** Accessible name. Defaults to "QuickXKart Home". */
  ariaLabel?: string;
  /** Override total width. Height auto-scales via aspect-ratio. */
  width?: number | string;
}

/**
 * QuickXKart brand mark + wordmark.
 *
 * The icon is a stylized cart riding a globe arc with a magenta speed-trail —
 * mirrors the company logo (blue + magenta gradient). Wordmark splits "QUICKX"
 * (navy) and "KART" (magenta) so the brand colour reads in both light and
 * dark contexts. Tagline appears on the `full` variant only.
 */
export function Logo({
  variant = 'compact',
  className,
  ariaLabel = 'QuickXKart Home',
  width,
}: LogoProps) {
  const showTagline = variant === 'full';
  const showWordmark = variant !== 'mark';

  return (
    <span
      role="img"
      aria-label={ariaLabel}
      className={cn(
        'inline-flex items-center gap-3 select-none',
        showTagline && 'flex-col items-start gap-1',
        className
      )}
      style={width !== undefined ? { width } : undefined}
    >
      <span className="inline-flex items-center gap-3">
        {/* Mark */}
        <img
          src="/cart-logo.png"
          alt=""
          width={variant === 'mark' ? '100%' : 44}
          height={variant === 'mark' ? '100%' : 44}
          aria-hidden
          style={{ flexShrink: 0, objectFit: 'contain' }}
        />

        {/* Wordmark */}
        {showWordmark && (
          <span
            className="font-[family-name:var(--font-display)] leading-none tracking-[0.02em]"
            style={{ fontSize: variant === 'full' ? '28px' : '22px' }}
          >
            {/* "QUICKX" was using --color-brand-navy-light (#1a2d8e).
                Restored blue color per user request. */}
            <span style={{ color: 'var(--color-brand-navy-light)' }}>QUICKX</span>
            <span style={{ color: 'var(--color-brand-magenta)' }}>KART</span>
          </span>
        )}
      </span>

      {showTagline && (
        <span
          className="text-label-mono"
          style={{ color: 'var(--color-text-muted)' }}
        >
          Premium Global Fashion &amp; Tech
          <span style={{ color: 'var(--color-brand-magenta)', margin: '0 6px' }}>|</span>
          Straight To Your Home
        </span>
      )}
    </span>
  );
}
